<template>
    <card-normal without-padding>
        <div v-if="loading">
            <spinner-loader />
        </div>
        <div v-else-if="statistics" class="statistics-tray">
            <statistics-entry
                v-for="(statistic, key) in displayData"
                :key="key"
                :title="statistic.title"
                :current-value="statistic.currentValue"
                :previous-value="statistic.previousValue"
                :render-type="statistic.renderType"
            >
                <template #difference-text>
                    {{ props.range.comparisonDescription }}
                </template>
            </statistics-entry>
        </div>
    </card-normal>
</template>

<script setup lang="ts">
    import { computed, onMounted, ref, watch } from 'vue';
    import { GetStatisticsForCompanyCommand, Range, StatisticsForCompany } from '@keyvue/one-api-client';
    import type StatisticDisplayData from '@/flow/statistics/types/StatisticDisplayData';
    import useClient from '@/services/keyvue-api/composables/useClient';
    import StatisticsEntry from '@/flow/statistics/components/StatisticsEntry.vue';

    interface Props {
        range: Range
    }

    const props = defineProps<Props>();
    const { client } = useClient();

    const loading = ref(false);
    const statistics = ref<StatisticsForCompany>();

    async function updateStatistics() {
        loading.value = true;
        statistics.value = await client.dispatch(new GetStatisticsForCompanyCommand(props.range));
        loading.value = false;
    }

    const displayData = computed<Record<string, StatisticDisplayData>>(() => {
        return {
            newFlowsCount: {
                title: 'Nieuw in flow',
                currentValue: statistics.value?.current.newFlowsCount ?? 0,
                previousValue: statistics.value?.previous?.newFlowsCount,
                renderType: 'count',
            },
            activeFlowsCount: {
                title: 'Actief in flow',
                currentValue: statistics.value?.current.activeFlowsCount ?? 0,
                previousValue: statistics.value?.previous?.activeFlowsCount,
                renderType: 'count',
            },
            successfulFlowsCount: {
                title: 'Voltooide opvolgingen',
                currentValue: statistics.value?.current.successfulFlowsCount ?? 0,
                previousValue: statistics.value?.previous?.successfulFlowsCount,
                renderType: 'count',
            },
            sentEmailsCount: {
                title: 'Verzonden e-mails',
                currentValue: statistics.value?.current.sentEmailsCount ?? 0,
                previousValue: statistics.value?.previous?.sentEmailsCount,
                renderType: 'percentage',
            },
            openedEmailsCount: {
                title: 'Geopende e-mails',
                currentValue: statistics.value?.current.openedEmailsCount ?? 0,
                previousValue: statistics.value?.previous?.openedEmailsCount,
                renderType: 'count',
            },
            clickedEmailsCount: {
                title: 'Geklikt in e-mail',
                currentValue: statistics.value?.current.clickedEmailsCount ?? 0,
                previousValue: statistics.value?.previous?.clickedEmailsCount,
                renderType: 'percentage',
            },
        };
    });

    onMounted(() => {
        updateStatistics();
    });

    watch(() => props.range, () => {
        updateStatistics();
    });
</script>

<style scoped lang="scss">
.statistics-tray {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    padding: size(sm);
    transition: background-color 0.2s;

    :deep(.statistics-entry) {
        border-right: 1px solid rgba(0, 0, 0, 0.1);

        &:last-child {
            border-right: none;
        }
    }
}

.text-heading {
    font-weight: 900;

    * {
        font-size: inherit;
    }
}

.text-decorator * {
    color: inherit;
}
</style>
